import { Badge, Button, FlexContent, ModalDialog, TableCell, TableHeadCell, TableRow } from "@rothoblaas/design-system";
import { FC, useContext, useRef, useState } from "react";
import { useAuthContext } from "../../context/authContext";
import NotificationDispatch, { showErrorNotification } from "../../context/notificationContext";
import useI18n from "../../hooks/useTranslations";
import { ACCESS_SCOPE_TITLES, AccessRule, AccessScope, UserAppAccess } from "../../http/types/accessControl";
import { getAccessRuleTarget, useAccessControlApi } from "../../http/useAccessControl";
import { useUsersApi } from "../../http/useUsers";
import AccessRuleModal from "../../pages/accessControl/AccessRuleModal";
import { getAddAction, getDeleteAction, getEditAction } from "../../utils/rowActions";
import Table from "../ui/Table";

// The user's access to each app, by their rule or else by the app's lock: read only, unless the
// current user manages the access control (not their own rules, which the backend refuses).
const AdminUserAccessRules: FC<{ userId: string; email?: string }> = ({ userId, email }) => {
  const i18n = useI18n();
  const auth = useAuthContext();
  const dispatch = useContext(NotificationDispatch);
  const usersApi = useUsersApi();
  const accessControlApi = useAccessControlApi();
  const aborter = useRef(new AbortController());

  const [scopeToEdit, setScopeToEdit] = useState<AccessScope>();
  const [ruleToDelete, setRuleToDelete] = useState<AccessRule>();
  const [isDeleteSubmitting, setIsDeleteSubmitting] = useState(false);

  const { data, error, mutate } = usersApi.getAdminUserAppAccesses(userId, aborter.current.signal);

  const canEdit = auth.permissions.admin.canManageAccessControl && !!email && userId !== auth.identity?.id;

  return (
    <FlexContent direction="column">
      <Table<UserAppAccess>
        head={
          <TableRow>
            <TableHeadCell align="left">{i18n.translation.accessControl.rules.app}</TableHeadCell>
            <TableHeadCell align="left">{i18n.translation.accessControl.rules.status}</TableHeadCell>
            <TableHeadCell align="left">{i18n.translation.accessControl.rules.reason}</TableHeadCell>
            <TableHeadCell align="left">{i18n.translation.accessControl.rules.createdAt}</TableHeadCell>
            {canEdit && <TableHeadCell></TableHeadCell>}
          </TableRow>
        }
        items={data}
        error={!!error}
        renderRow={(access) => (
          <>
            <TableCell>{ACCESS_SCOPE_TITLES[access.scope]}</TableCell>
            <TableCell>
              <Badge color={getStatusColor(access)}>
                {access.cause === "locked"
                  ? i18n.translation.accessControl.userAccess.notAllowed
                  : access.status === "allowed"
                    ? i18n.translation.accessControl.rules.statusAllowed
                    : i18n.translation.accessControl.rules.statusBlocked}
              </Badge>{" "}
              {i18n.translation.accessControl.userAccess.causes[access.cause]}
            </TableCell>
            <TableCell>{access.rule?.reason}</TableCell>
            <TableCell>{!!access.rule && new Date(access.rule.createdAt).toLocaleDateString()}</TableCell>
          </>
        )}
        rowActions={
          canEdit
            ? ({ scope, rule }) =>
                rule
                  ? [
                      getEditAction(i18n, () => setScopeToEdit(scope)),
                      getDeleteAction(i18n, () => setRuleToDelete(rule), true),
                    ]
                  : [getAddAction(i18n.translation.accessControl.rules.createNew, () => setScopeToEdit(scope))]
            : undefined
        }
      />

      {!!scopeToEdit && (
        <AccessRuleModal
          target={{ scope: scopeToEdit }}
          rule={data?.find((a) => a.scope === scopeToEdit)?.rule}
          email={email}
          onClose={() => {
            setScopeToEdit(undefined);
            mutate();
          }}
        />
      )}

      {!!ruleToDelete && (
        <ModalDialog
          onClose={() => setRuleToDelete(undefined)}
          title={i18n.translation.accessControl.rules.delete}
          isSubmitting={isDeleteSubmitting}
          buttons={[
            <Button key={0} onClick={() => setRuleToDelete(undefined)} variant="secondary">
              {i18n.translation.common.cancel}
            </Button>,
            <Button
              key={1}
              onClick={() => {
                setIsDeleteSubmitting(true);
                accessControlApi
                  .removeRule(getAccessRuleTarget(ruleToDelete), ruleToDelete.id)
                  .then(() => {
                    setRuleToDelete(undefined);
                    mutate();
                  })
                  .catch((err) => dispatch(showErrorNotification(i18n, err)))
                  .finally(() => setIsDeleteSubmitting(false));
              }}
            >
              {i18n.translation.common.yes}
            </Button>,
          ]}
        >
          {i18n.translation.accessControl.rules.questions.delete}
        </ModalDialog>
      )}
    </FlexContent>
  );
};

// A rule's own colors; grey when allowed without one, orange when the locked app keeps the user out.
const getStatusColor = ({ cause, status }: UserAppAccess) =>
  cause === "rule" ? (status === "allowed" ? "success" : "error") : cause === "locked" ? "warning" : "grey";

export default AdminUserAccessRules;
